'use client';

import { useEffect, useRef, useState } from 'react';
import { getToolAuthToken } from '@/lib/browser-tool-usage';
import { embedImageDpi, type DpiImageFormat } from '@/lib/image-dpi';

type LoadedImage = {
  element: HTMLImageElement;
  name: string;
  type: string;
  size: number;
  width: number;
  height: number;
};

const dpiPresets = [72, 150, 300, 600];

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function getBaseName(fileName: string) {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

function formatInches(pixels: number, dpi: number) {
  if (dpi <= 0) {
    return '—';
  }
  return `${(pixels / dpi).toFixed(2)}"`;
}

async function loadImageFromFile(file: File) {
  const objectUrl = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () =>
        reject(new Error('This image format is not supported in your browser yet.'));
      nextImage.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export default function BrowserImageDpiConverter() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [loadedImage, setLoadedImage] = useState<LoadedImage | null>(null);
  const [dpi, setDpi] = useState<number>(300);
  const [resample, setResample] = useState(false);
  const [targetWidth, setTargetWidth] = useState<number>(0);
  const [targetHeight, setTargetHeight] = useState<number>(0);
  const [lockAspect, setLockAspect] = useState(true);
  const [outputFormat, setOutputFormat] = useState<DpiImageFormat>('png');
  const [quality, setQuality] = useState(0.92);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [exportedSize, setExportedSize] = useState<number | null>(null);

  useEffect(() => {
    return () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  function resetOutputState() {
    if (downloadUrl) {
      URL.revokeObjectURL(downloadUrl);
    }
    setDownloadUrl(null);
    setDownloadName(null);
    setExportedSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      return;
    }

    try {
      const element = await loadImageFromFile(file);
      const width = element.naturalWidth;
      const height = element.naturalHeight;
      setLoadedImage({ element, name: file.name, type: file.type, size: file.size, width, height });
      setTargetWidth(width);
      setTargetHeight(height);
      setOutputFormat(/jpe?g/i.test(file.type) || /\.jpe?g$/i.test(file.name) ? 'jpeg' : 'png');
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'This image could not be opened in your browser.',
      );
    }
  }

  const effectiveWidth = resample && targetWidth > 0 ? targetWidth : (loadedImage?.width ?? 0);
  const effectiveHeight = resample && targetHeight > 0 ? targetHeight : (loadedImage?.height ?? 0);

  function updateWidth(nextValue: number) {
    const next = Math.max(1, Math.round(nextValue) || 1);
    setTargetWidth(next);
    if (lockAspect && loadedImage) {
      setTargetHeight(Math.max(1, Math.round((next * loadedImage.height) / loadedImage.width)));
    }
    resetOutputState();
  }

  function updateHeight(nextValue: number) {
    const next = Math.max(1, Math.round(nextValue) || 1);
    setTargetHeight(next);
    if (lockAspect && loadedImage) {
      setTargetWidth(Math.max(1, Math.round((next * loadedImage.width) / loadedImage.height)));
    }
    resetOutputState();
  }

  async function runExport() {
    if (!loadedImage) {
      setErrorMessage('Choose an image first.');
      return;
    }
    if (!Number.isFinite(dpi) || dpi < 1) {
      setErrorMessage('Enter a DPI of 1 or higher.');
      return;
    }

    setIsExporting(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: 'image-dpi-converter' }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another export right now.';
        throw new Error(message);
      }

      const outWidth = effectiveWidth || loadedImage.width;
      const outHeight = effectiveHeight || loadedImage.height;
      const canvas = document.createElement('canvas');
      canvas.width = outWidth;
      canvas.height = outHeight;
      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Your browser could not prepare the export.');
      }
      if (outputFormat === 'jpeg') {
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, outWidth, outHeight);
      }
      context.drawImage(loadedImage.element, 0, 0, outWidth, outHeight);

      const mimeType = outputFormat === 'png' ? 'image/png' : 'image/jpeg';
      const rawBlob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(
          (nextBlob) => resolve(nextBlob),
          mimeType,
          outputFormat === 'jpeg' ? quality : undefined,
        );
      });
      if (!rawBlob) {
        throw new Error('The export could not finish for this image.');
      }

      const blob = await embedImageDpi(rawBlob, Math.round(dpi), outputFormat);
      const nextDownloadUrl = URL.createObjectURL(blob);
      const extension = outputFormat === 'png' ? 'png' : 'jpg';
      const nextFileName = `${getBaseName(loadedImage.name)}-${Math.round(dpi)}dpi.${extension}`;

      setDownloadUrl(nextDownloadUrl);
      setDownloadName(nextFileName);
      setExportedSize(blob.size);
      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'Unlimited plan active.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : null,
      );
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'The image could not be exported.');
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Change image DPI in the browser</h2>
          <p>
            Upload one image, set the print resolution, and export a PNG or JPG with the DPI
            embedded.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-routes" aria-label="Common DPI presets">
        {dpiPresets.map((preset) => (
          <button
            key={preset}
            className={`tool-live-route ${dpi === preset ? 'tool-live-route-active' : ''}`}
            onClick={() => {
              setDpi(preset);
              resetOutputState();
            }}
            type="button"
          >
            {preset} DPI
          </button>
        ))}
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{loadedImage ? loadedImage.name : 'Choose an image or drop it here'}</strong>
              <span>
                {loadedImage
                  ? `${formatFileSize(loadedImage.size)} • ${loadedImage.type || 'Image file'}`
                  : 'Works with PNG and JPG for print-ready DPI'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/png,image/jpeg"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="field">
              <span>DPI</span>
              <input
                min="1"
                onChange={(event) => {
                  setDpi(Math.max(1, Number(event.target.value) || 1));
                  resetOutputState();
                }}
                type="number"
                value={dpi}
              />
            </label>
            <label className="field">
              <span>Output format</span>
              <select
                onChange={(event) => {
                  setOutputFormat(event.target.value as DpiImageFormat);
                  resetOutputState();
                }}
                value={outputFormat}
              >
                <option value="png">PNG</option>
                <option value="jpeg">JPG</option>
              </select>
            </label>
            <label className="field">
              <span>Resample pixels</span>
              <select
                onChange={(event) => {
                  setResample(event.target.value === 'resize');
                  resetOutputState();
                }}
                value={resample ? 'resize' : 'keep'}
              >
                <option value="keep">Keep original pixels</option>
                <option value="resize">Change pixel size</option>
              </select>
            </label>
          </div>

          {resample ? (
            <div className="tool-live-controls">
              <label className="field">
                <span>Width (px)</span>
                <input
                  min="1"
                  onChange={(event) => updateWidth(Number(event.target.value))}
                  type="number"
                  value={targetWidth}
                />
              </label>
              <label className="field">
                <span>Height (px)</span>
                <input
                  min="1"
                  onChange={(event) => updateHeight(Number(event.target.value))}
                  type="number"
                  value={targetHeight}
                />
              </label>
              <label className="field">
                <span>Lock aspect</span>
                <select
                  onChange={(event) => setLockAspect(event.target.value === 'on')}
                  value={lockAspect ? 'on' : 'off'}
                >
                  <option value="on">On</option>
                  <option value="off">Off</option>
                </select>
              </label>
            </div>
          ) : null}

          {outputFormat === 'jpeg' ? (
            <div className="tool-live-quality-shell">
              <label className="field tool-live-quality">
                <span>JPG quality</span>
                <input
                  max="0.98"
                  min="0.4"
                  onChange={(event) => {
                    setQuality(Number(event.target.value));
                    resetOutputState();
                  }}
                  step="0.02"
                  type="range"
                  value={quality}
                />
                <small>Used for JPG exports</small>
              </label>
            </div>
          ) : null}

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!loadedImage || isExporting}
              onClick={() => void runExport()}
              type="button"
            >
              {isExporting ? 'Exporting…' : `Export at ${Math.round(dpi)} DPI`}
            </button>
            {downloadUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={downloadUrl}>
                Download {outputFormat === 'png' ? 'PNG' : 'JPG'}
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Set the right DPI for print</h3>
          <div className="tool-mockup-chip-list">
            <span>72 web</span>
            <span>150 draft</span>
            <span>300 print</span>
            <span>600 fine</span>
          </div>
          <p>
            DPI sets how large the image prints. At a fixed pixel size, a higher DPI prints smaller
            and sharper. 300 DPI is the standard for photo and document printing.
          </p>
          <div className="tool-live-panel-note">
            <strong>Runs in your browser</strong>
            <span>The DPI is written into the file locally. Nothing is uploaded to a server.</span>
          </div>
          {loadedImage ? (
            <div className="tool-live-stats">
              <div>
                <strong>Source</strong>
                <span>{formatFileSize(loadedImage.size)}</span>
              </div>
              <div>
                <strong>Pixels</strong>
                <span>{`${effectiveWidth} × ${effectiveHeight}`}</span>
              </div>
              <div>
                <strong>Print size</strong>
                <span>{`${formatInches(effectiveWidth, dpi)} × ${formatInches(effectiveHeight, dpi)}`}</span>
              </div>
              {exportedSize ? (
                <div>
                  <strong>Result</strong>
                  <span>{formatFileSize(exportedSize)}</span>
                </div>
              ) : null}
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>
    </article>
  );
}
